import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ApiError, fetchAgents } from '../../lib/api';
import {
  emitSetWatchedPaths,
  fetchWatchedPaths,
  type FsEventType,
  type WatchedPathInput,
} from '../../lib/watchedPaths';

const ALL_EVENT_TYPES: readonly FsEventType[] = ['created', 'modified', 'deleted', 'renamed'];

interface DraftPath {
  path: string;
  recursive: boolean;
  eventTypes: FsEventType[];
  whitelistRaw: string;
  blacklistRaw: string;
}

function emptyDraft(): DraftPath {
  return {
    path: '',
    recursive: true,
    eventTypes: [...ALL_EVENT_TYPES],
    whitelistRaw: '',
    blacklistRaw: '',
  };
}

function parseExtensions(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0)
    .map((s) => (s.startsWith('.') ? s : `.${s}`));
}

export function WatchedPathsPage(): JSX.Element {
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [drafts, setDrafts] = useState<DraftPath[]>([emptyDraft()]);
  const [justification, setJustification] = useState<string>('');
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const agentsQuery = useQuery({ queryKey: ['agents'], queryFn: fetchAgents });
  const queryClient = useQueryClient();

  const watchedQuery = useQuery({
    queryKey: ['watchedPaths', selectedAgentId],
    queryFn: () => fetchWatchedPaths(selectedAgentId),
    enabled: selectedAgentId !== '',
  });

  useEffect(() => {
    if (watchedQuery.data && watchedQuery.data.items.length > 0) {
      setDrafts(
        watchedQuery.data.items.map((it) => ({
          path: it.path,
          recursive: it.recursive,
          eventTypes: [...it.eventTypes],
          whitelistRaw: it.extensionsWhitelist.join('\n'),
          blacklistRaw: it.extensionsBlacklist.join('\n'),
        }))
      );
    } else if (watchedQuery.data && watchedQuery.data.items.length === 0) {
      setDrafts([emptyDraft()]);
    }
  }, [watchedQuery.data]);

  const applyMutation = useMutation({
    mutationFn: () => {
      const cleaned: WatchedPathInput[] = drafts
        .filter((d) => d.path.trim() !== '')
        .map((d) => {
          const wl = parseExtensions(d.whitelistRaw);
          const bl = parseExtensions(d.blacklistRaw);
          return {
            path: d.path.trim(),
            recursive: d.recursive,
            eventTypes: d.eventTypes,
            ...(wl.length > 0 ? { extensionsWhitelist: wl } : {}),
            ...(bl.length > 0 ? { extensionsBlacklist: bl } : {}),
          };
        });
      if (cleaned.length === 0) {
        throw new Error('Adăugați cel puțin un path valid.');
      }
      if (justification.trim().length < 10) {
        throw new Error('Justificare obligatorie (minim 10 caractere).');
      }
      return emitSetWatchedPaths(selectedAgentId, cleaned, justification.trim());
    },
    onSuccess: (data) => {
      setFeedback({
        kind: 'success',
        text: `Set watched paths emit OK (${data.pathsCount} paths) — WS direct.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['watchedPaths', selectedAgentId] });
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError
          ? err.messages[0]?.message
          : err instanceof Error
            ? err.message
            : 'Eroare la apply.';
      setFeedback({ kind: 'error', text: msg ?? 'Eroare la apply.' });
    },
  });

  function updateDraft(idx: number, patch: Partial<DraftPath>): void {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  }

  function addDraft(): void {
    if (drafts.length >= 10) return;
    setDrafts((prev) => [...prev, emptyDraft()]);
  }

  function removeDraft(idx: number): void {
    setDrafts((prev) => prev.filter((_, i) => i !== idx));
  }

  function toggleEventType(idx: number, et: FsEventType): void {
    setDrafts((prev) =>
      prev.map((d, i) => {
        if (i !== idx) return d;
        const has = d.eventTypes.includes(et);
        return {
          ...d,
          eventTypes: has ? d.eventTypes.filter((e) => e !== et) : [...d.eventTypes, et],
        };
      })
    );
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    setFeedback(null);
    applyMutation.mutate();
  }

  const agents = agentsQuery.data ?? [];

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Watched paths (FsWatcher per agent)
      </Typography>

      <FormControl size="small" sx={{ minWidth: 320, mb: 3 }}>
        <InputLabel>Agent</InputLabel>
        <Select
          label="Agent"
          value={selectedAgentId}
          onChange={(e) => setSelectedAgentId(e.target.value)}
        >
          {agents.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.hostname}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedAgentId === '' && (
        <Alert severity="info">Selectați un agent pentru a configura watched paths.</Alert>
      )}

      {selectedAgentId !== '' && watchedQuery.isLoading && <CircularProgress />}

      {selectedAgentId !== '' && watchedQuery.data && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Curent active: {watchedQuery.data.count} paths
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Path</TableCell>
                <TableCell>Recursive</TableCell>
                <TableCell>Event types</TableCell>
                <TableCell>Whitelist</TableCell>
                <TableCell>Blacklist</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {watchedQuery.data.items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell>{it.path}</TableCell>
                  <TableCell>{it.recursive ? 'da' : 'nu'}</TableCell>
                  <TableCell>
                    {it.eventTypes.map((et) => (
                      <Chip key={et} label={et} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>{it.extensionsWhitelist.join(', ') || '—'}</TableCell>
                  <TableCell>{it.extensionsBlacklist.join(', ') || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {selectedAgentId !== '' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Set new (DELETE+INSERT — înlocuiește lista actuală)
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {drafts.map((d, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <TextField
                        label="Path absolute Windows"
                        value={d.path}
                        onChange={(e) => updateDraft(idx, { path: e.target.value })}
                        size="small"
                        fullWidth
                        placeholder="C:\Confidential"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={d.recursive}
                            onChange={(e) => updateDraft(idx, { recursive: e.target.checked })}
                          />
                        }
                        label="Recursive"
                      />
                      {drafts.length > 1 && (
                        <IconButton onClick={() => removeDraft(idx)} size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      {ALL_EVENT_TYPES.map((et) => (
                        <FormControlLabel
                          key={et}
                          control={
                            <Checkbox
                              size="small"
                              checked={d.eventTypes.includes(et)}
                              onChange={() => toggleEventType(idx, et)}
                            />
                          }
                          label={et}
                        />
                      ))}
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="Whitelist extensions (.docx, .pdf — câte una pe linie)"
                        value={d.whitelistRaw}
                        onChange={(e) => updateDraft(idx, { whitelistRaw: e.target.value })}
                        size="small"
                        multiline
                        minRows={2}
                        fullWidth
                      />
                      <TextField
                        label="Blacklist extensions (.tmp, .log)"
                        value={d.blacklistRaw}
                        onChange={(e) => updateDraft(idx, { blacklistRaw: e.target.value })}
                        size="small"
                        multiline
                        minRows={2}
                        fullWidth
                      />
                    </Stack>
                  </Stack>
                </Paper>
              ))}
              <Stack direction="row" spacing={1}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addDraft}
                  disabled={drafts.length >= 10}
                  size="small"
                >
                  Adaugă path ({drafts.length}/10)
                </Button>
              </Stack>
              <TextField
                label="Justificare (min 10 caractere — apare în audit_log)"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                size="small"
                multiline
                minRows={2}
                fullWidth
              />
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={applyMutation.isPending}
                >
                  {applyMutation.isPending ? 'Aplicare…' : 'Apply (înlocuiește toate paths)'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>
      )}

      {feedback && (
        <Alert severity={feedback.kind} sx={{ mt: 2 }} onClose={() => setFeedback(null)}>
          {feedback.text}
        </Alert>
      )}
    </Box>
  );
}
